import { Navigate, useLocation } from 'react-router-dom';
import { Spin } from 'antd';
import { trpc } from '../../lib/api';
import { useAuthStore } from '../../stores/authStore';

interface SubscriptionExpiredGuardProps {
  children: React.ReactNode;
}

/**
 * サブスクリプションが期限切れ（canceled）の場合、
 * /app/subscription 以外のページへのアクセスをブロックするガード
 */
export function SubscriptionExpiredGuard({ children }: SubscriptionExpiredGuardProps) {
  const location = useLocation();
  const { isAuthenticated } = useAuthStore();

  const { data: company, isLoading, error } = trpc.company.getCurrentCompany.useQuery(
    undefined,
    {
      enabled: isAuthenticated,
      retry: false,
      staleTime: 30000, // 30秒間キャッシュ
    }
  );

  // 読み込み中
  if (isLoading) {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100vh',
      }}>
        <Spin size="large" />
      </div>
    );
  }

  // エラー時は通常表示（エラーは別で処理される）
  if (error) {
    return <>{children}</>;
  }

  // サブスクリプションがcanceled状態かチェック
  const isSubscriptionCanceled = company?.subscriptionStatus === 'canceled';

  // 許可されるパス
  const allowedPaths = [
    '/app/subscription',
    '/app/settings/subscription', // 実際のサブスクリプションページ（/app/subscription からリダイレクトされる）
    '/app/profile', // プロフィールページは許可（ログアウトのため）
  ];

  // 現在のパスが許可されているかチェック
  const isAllowedPath = allowedPaths.some(path =>
    location.pathname === path || location.pathname.startsWith(`${path}/`)
  );

  // サブスクリプションが期限切れで、許可されていないパスにアクセスしようとした場合
  if (isSubscriptionCanceled && !isAllowedPath) {
    return <Navigate to="/app/subscription" replace />;
  }

  return <>{children}</>;
}
